import { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Section } from '@carouselgg/nexus/section'
import { Grid } from '@carouselgg/nexus/grid'
import { Card } from '@carouselgg/nexus/card'
import { StatCard } from '@carouselgg/nexus/stat-card'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { Badge } from '@carouselgg/nexus/badge'
import {
  Trophy, Users, CalendarDays,
  TrophyCup, ChevronRight,
} from '@carouselgg/nexus/icon'
import { LogoMarkWhite } from '@carouselgg/nexus/brand'
import { useAuthStore } from '@carouselgg/auth/store'
import { listPlatformArticles } from '../api/content'
import type { PlatformArticle } from '../api/content'
import { getPlatformStats, listPlatformResults } from '../api/platform'
import type { PlatformStats, PlatformResult } from '../api/platform'
import { SignedInDashboard } from './dashboard/SignedInDashboard'
import { StartHere } from './dashboard/home/StartHere'
import { OpenCompetitions } from './dashboard/home/OpenCompetitions'
import { JoinPanel } from './dashboard/home/JoinPanel'
import homeStyles from './dashboard/home/home.module.css'
import { ActionLink } from '../components/ActionLink'
import { discoverSeasons, type DiscoverSeason } from '../api/compete'
import styles from './HomePage.module.css'

/**
 * The dashboard, which serves signed-in and signed-out visitors from one route.
 *
 * Signed in, it is a work surface: what needs you, what is coming up, what you
 * run. Signed out it is a shop window onto real platform activity — live
 * counters and recent results, not the marketing pitch, which lives on `/`.
 */

// Branded fallback covers for articles without an uploaded image (matches the
// marketing LatestNews section), varied by index so a row isn't all identical.
const COVER_GRADIENTS = [
  'linear-gradient(135deg, var(--nexus-purple), var(--nexus-pink))',
  'linear-gradient(135deg, var(--nexus-blue), var(--nexus-green))',
  'linear-gradient(135deg, var(--nexus-pink), var(--nexus-purple), var(--nexus-blue))',
]


function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

export function HomePage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return isAuthenticated ? <SignedInDashboard /> : <SignedOutDashboard />
}

function SignedOutDashboard() {
  const [news, setNews] = useState<PlatformArticle[]>([])
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [results, setResults] = useState<PlatformResult[]>([])
  const [open, setOpen] = useState<DiscoverSeason[]>([])
  const [now] = useState(() => new Date())

  useEffect(() => {
    let cancelled = false
    // Independent public reads; any one being down costs its own band only.
    Promise.allSettled([listPlatformArticles(4, 0), getPlatformStats(), listPlatformResults(6), discoverSeasons(undefined, 4)])
      .then(([newsRes, statsRes, resultsRes, openRes]) => {
        if (cancelled) return
        if (openRes.status === 'fulfilled') setOpen(openRes.value.seasons)
        if (newsRes.status === 'fulfilled') setNews(newsRes.value.articles)
        if (statsRes.status === 'fulfilled') setStats(statsRes.value)
        if (resultsRes.status === 'fulfilled') setResults(resultsRes.value)
      })
    return () => { cancelled = true }
  }, [])

  return (
    <div className={styles.page}>
      <Section spacing="lg">
        <Container size="xl">
          <div className={styles.welcome}>
            {/* The same page a brand-new account sees, with every way in going
                through sign-up first. Creating the account used to sit only at
                the very bottom, below the news. */}
            <header className={styles.welcomeHead}>
              <Text size="xs" muted className={styles.welcomeEyebrow}>Welcome to Carousel</Text>
              <h1 className={styles.welcomeTitle}>
                Esports for <GradientText>everyone.</GradientText>
              </h1>
              <Text size="lg" muted>Go solo, bring your squad, or run the show. One account does all of it.</Text>
            </header>
            <StartHere signedOut />
            <div className={homeStyles.newHere}>
              <OpenCompetitions seasons={open} now={now} />
              <JoinPanel />
            </div>
            <div className={homeStyles.band}>
              <div className={homeStyles.bandText}>
                <h2 className={homeStyles.sectionTitle}>Joining friends?</h2>
                <p className={homeStyles.bandBody}>Ask your captain for the team’s invite link, or browse clubs that are recruiting.</p>
              </div>
              <ActionLink to="/orgs" variant="secondary">Browse clubs</ActionLink>
            </div>
          </div>
        </Container>
      </Section>

      {/* Live counters. These are real counts, so they are rendered whatever
          they say — a quiet platform showing quiet numbers beats a fake one. */}
      {stats && (
        <Section spacing="lg">
          <Container size="lg">
            <Grid cols={4} colsMd={2} gap="md">
              <StatCard
                icon={<TrophyCup size={24} />}
                value={String(stats.leagues)}
                label={stats.leagues === 1 ? 'League' : 'Leagues'}
                accent="var(--nexus-purple)"
              />
              <StatCard
                icon={<Users size={24} />}
                value={String(stats.players)}
                label={stats.players === 1 ? 'Player on a roster' : 'Players on rosters'}
                accent="var(--nexus-blue)"
              />
              <StatCard
                icon={<CalendarDays size={24} />}
                value={String(stats.live_seasons)}
                label={stats.live_seasons === 1 ? 'Season running' : 'Seasons running'}
                accent="var(--nexus-pink)"
              />
              <StatCard
                icon={<Trophy size={24} />}
                value={String(stats.matches_played)}
                label="Matches played"
                accent="var(--nexus-green)"
              />
            </Grid>
          </Container>
        </Section>
      )}

      {results.length > 0 && (
        <Section spacing="lg">
          <Container size="lg">
            <div className={styles.sectionHeader}>
              <Text as="h2" size="xl" weight="bold">Recent results</Text>
              <Link to="/compete">
                <Button variant="ghost" size="sm">Find a league <ChevronRight size={14} /></Button>
              </Link>
            </div>
            <Grid cols={2} colsSm={1} gap="md">
              {results.map((result) => (
                <Card key={result.match_id} padding="lg">
                  <div className={styles.resultCard}>
                    <div className={styles.resultMeta}>
                      <Badge variant="default" size="sm">{result.game_name}</Badge>
                      <Link to={`/org/${result.organization_slug}`} className={styles.resultOrg}>
                        <Text size="xs" muted>{result.organization_name}</Text>
                      </Link>
                    </div>
                    {result.teams.map((team) => (
                      <div key={team.team_id} className={styles.resultTeam}>
                        <Text weight={team.won ? 'semibold' : 'normal'} muted={!team.won}>
                          {team.name}
                        </Text>
                        <Text weight={team.won ? 'semibold' : 'normal'} muted={!team.won}>
                          {team.score}
                        </Text>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </Grid>
          </Container>
        </Section>
      )}

      {news.length > 0 && (
        <Section spacing="lg">
          <Container size="lg">
            <div className={styles.sectionHeader}>
              <Text as="h2" size="xl" weight="bold">Latest News</Text>
              <Link to="/news"><Button variant="ghost" size="sm">All News <ChevronRight size={14} /></Button></Link>
            </div>
            <Grid cols={2} colsSm={1} gap="md">
              {news.map((article, i) => (
                <Link key={article.id} to={`/news/${article.slug}`} className={styles.articleLink}>
                  <Card interactive accent="var(--nexus-purple)" className={styles.articleCardWrap}>
                    {article.cover_image_url ? (
                      <div className={styles.articleImage}>
                        <img src={article.cover_image_url} alt="" />
                      </div>
                    ) : (
                      <div
                        className={`${styles.articleImage} ${styles.articlePlaceholder}`}
                        style={{ background: COVER_GRADIENTS[i % COVER_GRADIENTS.length] }}
                      >
                        <img src={LogoMarkWhite} alt="" />
                      </div>
                    )}
                    <div className={styles.articleCard}>
                      <div className={styles.articleMeta}>
                        <Badge variant="default" size="sm">{article.tags[0] ?? 'News'}</Badge>
                        <Text size="xs" muted>{formatDate(article.published_at)}</Text>
                      </div>
                      <Text as="h3" size="base" weight="semibold">{article.title}</Text>
                      <Text size="sm" muted>{article.excerpt}</Text>
                    </div>
                  </Card>
                </Link>
              ))}
            </Grid>
          </Container>
        </Section>
      )}

    </div>
  )
}
